import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Row, Col, Spinner, Alert, Nav } from 'react-bootstrap';
import {
  BarChart2, PieChart as PieIcon, Download, Users, ChevronDown, ChevronUp,
  ChevronLeft, ChevronRight,
  Briefcase, Building2, LayoutDashboard, FileText, TrendingUp, TrendingDown,
    BarChart3
} from 'lucide-react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
} from 'recharts';
import PageHeader from '../../../layouts/PageHeader';
import * as api from '../../../../services/api/timeReportingApi';
import * as S from '../../Common/internalToolsStyles';

const TABS = [
  { key: 'per-employee', label: 'Per anställd', icon: Users },
  { key: 'per-project',  label: 'Per projekt',  icon: Briefcase },
  { key: 'per-customer', label: 'Per kund',     icon: Building2 },
  { key: 'summary',      label: 'Sammanfattning', icon: LayoutDashboard },
  { key: 'utilization',  label: 'Beläggning',   icon: BarChart2 },
  { key: 'distribution', label: 'Tidsfördelning', icon: PieIcon },
];

// Stable palette for charts. Falls back through the array when projects
// don't have their own .color set.
const CHART_PALETTE = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16', '#ec4899'];

const fmtNum = (n) => n != null ? Math.round(n * 100) / 100 : '—';
const fmtPct = (n) => n != null ? Math.round(n * 10) / 10 + '%' : '—';

// ─── Date helpers ───
//
// Presets are computed from "now" at render time so a user keeping the
// page open across midnight gets fresh ranges on the next refresh.
const isoDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const startOfWeekMonday = (d) => {
  const dt = new Date(d);
  const dow = dt.getDay(); // 0 Sun..6 Sat
  const daysBack = dow === 0 ? 6 : dow - 1;
  dt.setDate(dt.getDate() - daysBack);
  dt.setHours(0, 0, 0, 0);
  return dt;
};
const addDays = (d, n) => { const dt = new Date(d); dt.setDate(dt.getDate() + n); return dt; };

// ─── Period navigator math ───
//
// Range computed from (granularity, offset). Offset 0 = current period,
// -1 = previous, +1 = next. Same model as TimesheetPage's week stepper,
// just generalized to weeks/months. Custom mode opts out of the math
// and lets the user pick arbitrary start/end dates (a quarter, a fiscal
// year, etc.).
const ISO_WEEKDAY_NAMES = ['Sön', 'Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör'];
const SWEDISH_MONTHS = [
  'januari', 'februari', 'mars', 'april', 'maj', 'juni',
  'juli', 'augusti', 'september', 'oktober', 'november', 'december',
];
const SWEDISH_MONTHS_SHORT = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

// ISO week number — same formula as the heatmap helper at the bottom of
// this file. Duplicated here because that helper is declared later;
// keeping it inline avoids a hoisting headache.
const isoWeekNumber = (d) => {
  const dt = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = dt.getUTCDay() || 7;
  dt.setUTCDate(dt.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(dt.getUTCFullYear(), 0, 1));
  return Math.ceil((((dt - yearStart) / 86400000) + 1) / 7);
};

const rangeFromGranularity = (granularity, offset) => {
  const now = new Date();
  if (granularity === 'week') {
    const baseStart = startOfWeekMonday(now);
    const start = addDays(baseStart, offset * 7);
    const end = addDays(start, 6);
    return { startDate: isoDate(start), endDate: isoDate(end) };
  }
  if (granularity === 'month') {
    const ref = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    return { startDate: isoDate(startOfMonth(ref)), endDate: isoDate(endOfMonth(ref)) };
  }
  return null; // custom — caller manages dates manually
};

const labelForRange = (granularity, offset, startDate, endDate) => {
  if (granularity === 'week') {
    const start = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T00:00:00');
    const week = isoWeekNumber(start);
    const sd = start.getDate();
    const ed = end.getDate();
    const sm = SWEDISH_MONTHS_SHORT[start.getMonth()];
    const em = SWEDISH_MONTHS_SHORT[end.getMonth()];
    const sy = start.getFullYear();
    const ey = end.getFullYear();
    let prefix = '';
    if (offset === 0) prefix = 'Denna vecka · ';
    else if (offset === -1) prefix = 'Förra veckan · ';
    const range = (sm === em && sy === ey)
      ? `${sd}–${ed} ${em} ${ey}`
      : sy === ey
        ? `${sd} ${sm} – ${ed} ${em} ${ey}`
        : `${sd} ${sm} ${sy} – ${ed} ${em} ${ey}`;
    return `${prefix}v${week} · ${range}`;
  }
  if (granularity === 'month') {
    const ref = new Date(startDate + 'T00:00:00');
    const monthName = SWEDISH_MONTHS[ref.getMonth()];
    let prefix = '';
    if (offset === 0) prefix = 'Denna månad · ';
    else if (offset === -1) prefix = 'Förra månaden · ';
    return `${prefix}${monthName.charAt(0).toUpperCase()}${monthName.slice(1)} ${ref.getFullYear()}`;
  }
  return `${startDate} – ${endDate}`;
};

const ReportsPage = () => {
  const { companyId } = useParams();
  const [activeTab, setActiveTab] = useState('per-employee');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // Period navigator: (granularity, offset) is the source of truth for
  // week/month modes; startDate/endDate are derived. Custom mode flips
  // the relationship — the user owns the dates directly.
  const initialRange = rangeFromGranularity('month', 0);
  const [granularity, setGranularity] = useState('month');
  const [periodOffset, setPeriodOffset] = useState(0);
  const [startDate, setStartDate] = useState(initialRange.startDate);
  const [endDate, setEndDate] = useState(initialRange.endDate);

  // Per-employee status filter: 'all' (default) or 'approved'. Payroll
  // typically wants 'approved' before running wage calculations.
  const [statusFilter, setStatusFilter] = useState('all');

  // Switching granularity always resets to the current period — it would
  // be confusing to land on "förra månaden of three weeks ago" because
  // the offset got reinterpreted across modes.
  const handleGranularityChange = (g) => {
    setGranularity(g);
    setPeriodOffset(0);
    if (g !== 'custom') {
      const r = rangeFromGranularity(g, 0);
      setStartDate(r.startDate);
      setEndDate(r.endDate);
    }
  };

  const handleStep = (delta) => {
    if (granularity === 'custom') return;
    const next = periodOffset + delta;
    setPeriodOffset(next);
    const r = rangeFromGranularity(granularity, next);
    setStartDate(r.startDate);
    setEndDate(r.endDate);
  };

  const handleJumpToCurrent = () => {
    if (granularity === 'custom') return;
    setPeriodOffset(0);
    const r = rangeFromGranularity(granularity, 0);
    setStartDate(r.startDate);
    setEndDate(r.endDate);
  };

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    setExpandedId(null);
    try {
      let result;
      switch (activeTab) {
        case 'per-employee':
          result = await api.getPerEmployeeReport(companyId, { startDate, endDate, statusFilter });
          setData(result.data.data);
          break;
        case 'per-project':
          result = await api.getPerProjectReport(companyId, { startDate, endDate, statusFilter });
          setData(result.data.data);
          break;
        case 'per-customer':
          result = await api.getPerCustomerReport(companyId, { startDate, endDate, statusFilter });
          setData(result.data.data);
          break;
        case 'summary': {
          // Sammanfattning needs per-employee data too for the cross-team
          // stacked bar, so fetch both in parallel and bundle the result.
          const [summaryRes, perEmpRes] = await Promise.all([
            api.getSummaryReport(companyId, { startDate, endDate, statusFilter }),
            api.getPerEmployeeReport(companyId, { startDate, endDate, statusFilter }),
          ]);
          setData({ summary: summaryRes.data.data, perEmployee: perEmpRes.data.data });
          break;
        }
        case 'utilization':
          result = await api.getUtilizationReport(companyId, { startDate, endDate });
          setData(result.data.data);
          break;
        case 'distribution':
          result = await api.getDistributionReport(companyId, { startDate, endDate });
          setData(result.data.data);
          break;
        default:
          setData(null);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte ladda rapport');
    } finally {
      setLoading(false);
    }
  }, [companyId, activeTab, startDate, endDate, statusFilter]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const handleExport = async (format = 'csv') => {
    try {
      const params = { type: activeTab, startDate, endDate, format };
      if (['per-employee', 'per-project', 'per-customer', 'summary'].includes(activeTab)) {
        params.statusFilter = statusFilter;
      }
      const res = await api.exportReportCsv(companyId, params);
      const ext = format === 'pdf' ? 'pdf' : 'csv';
      const blob = new Blob([res.data], { type: format === 'pdf' ? 'application/pdf' : 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport-${activeTab}-${startDate}_${endDate}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Kunde inte exportera rapport');
    }
  };

  // ─── Per anställd ───

  const teamTotals = useMemo(() => {
    if (activeTab !== 'per-employee' || !Array.isArray(data)) return null;
    return data.reduce((acc, r) => ({
      total_work_hours: acc.total_work_hours + (Number(r.total_work_hours) || 0),
      billable_hours: acc.billable_hours + (Number(r.billable_hours) || 0),
      total_leave_hours: acc.total_leave_hours + (Number(r.total_leave_hours) || 0),
      total_hours: acc.total_hours + (Number(r.total_hours) || 0),
      approved_hours: acc.approved_hours + (Number(r.approved_hours) || 0),
    }), { total_work_hours: 0, billable_hours: 0, total_leave_hours: 0, total_hours: 0, approved_hours: 0 });
  }, [activeTab, data]);

  const renderPerEmployee = () => {
    if (!Array.isArray(data) || data.length === 0) {
      return (
        <div style={S.emptyState}>
          <div style={S.emptyStateIcon}><Users size={48} /></div>
          <h4 style={S.emptyStateTitle}>Inga anställda att visa</h4>
          <p style={S.emptyStateDesc}>
            Inga aktiva anställda för perioden, eller inget datum valt.
          </p>
        </div>
      );
    }
    return (
      <table style={S.table}>
        <thead>
          <tr style={S.tableHeaderRow}>
            <th style={{ ...S.tableHeaderCell, width: 28 }} />
            <th style={S.tableHeaderCell}>Anställd</th>
            <th style={{ ...S.tableHeaderCell, textAlign: 'right' }}>Ordinarie</th>
            <th style={{ ...S.tableHeaderCell, textAlign: 'right' }} title="Övertid 1 + 2">Övertid</th>
            <th style={{ ...S.tableHeaderCell, textAlign: 'right' }}>Komp</th>
            <th style={{ ...S.tableHeaderCell, textAlign: 'right' }}>Frånvaro</th>
            <th style={{ ...S.tableHeaderCell, textAlign: 'right' }}>Debiterbart</th>
            <th style={{ ...S.tableHeaderCell, textAlign: 'right' }}>Godkänt</th>
            <th style={{ ...S.tableHeaderCell, textAlign: 'right' }}>Total</th>
            <th style={{ ...S.tableHeaderCell, width: 110, textAlign: 'left' }}>Mönster</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row) => {
            const overtime = (Number(row.overtime_1_hours) || 0) + (Number(row.overtime_2_hours) || 0);
            const approvedPct = row.total_hours > 0 ? (row.approved_hours / row.total_hours) * 100 : 0;
            const isExpanded = expandedId === row.employee_id;
            return (
              <React.Fragment key={row.employee_id}>
                <tr
                  style={{ ...S.tableRow, cursor: 'pointer' }}
                  onClick={() => setExpandedId(isExpanded ? null : row.employee_id)}
                >
                  <td style={S.tableCell}>
                    {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </td>
                  <td style={S.tableCell}>
                    <span style={S.text.bodySmallMedium}>{row.firstname} {row.lastname}</span>
                  </td>
                  <td style={{ ...S.tableCell, textAlign: 'right', ...S.text.number }}>{fmtNum(row.ordinary_hours)}h</td>
                  <td style={{ ...S.tableCell, textAlign: 'right', ...S.text.number, color: overtime > 0 ? S.colors.warning : undefined }}>
                    {overtime > 0 ? `${fmtNum(overtime)}h` : '—'}
                  </td>
                  <td style={{ ...S.tableCell, textAlign: 'right', ...S.text.number }}>
                    {row.comp_earned_hours > 0 ? `${fmtNum(row.comp_earned_hours)}h` : '—'}
                  </td>
                  <td style={{ ...S.tableCell, textAlign: 'right', ...S.text.number }}>
                    {row.total_leave_hours > 0 ? `${fmtNum(row.total_leave_hours)}h` : '—'}
                  </td>
                  <td style={{ ...S.tableCell, textAlign: 'right', ...S.text.number }}>{fmtNum(row.billable_hours)}h</td>
                  <td style={{ ...S.tableCell, textAlign: 'right' }}>
                    <span style={{
                      color: approvedPct >= 99 ? S.colors.success
                        : approvedPct >= 50 ? S.colors.warning
                          : S.colors.danger,
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      {fmtPct(approvedPct)}
                    </span>
                  </td>
                  <td style={{ ...S.tableCell, textAlign: 'right', ...S.text.bodySmallMedium, ...S.text.number }}>
                    {fmtNum(row.total_hours)}h
                  </td>
                  <td style={{ ...S.tableCell, width: 110 }}>
                    <DailySparkline daily={row.daily_hours} />
                  </td>
                </tr>
                {isExpanded && (
                  <tr>
                    <td colSpan={10} style={{ background: S.colors.bgSubtle, padding: '12px 24px' }}>
                      <Row>
                        <Col md={3}>
                          <h6 style={S.text.label}>Fördelning</h6>
                          <EmployeeMixDonut row={row} />
                        </Col>
                        <Col md={3}>
                          <h6 style={S.text.label}>Arbete (per kategori)</h6>
                          <DetailRow label="Ordinarie" value={fmtNum(row.ordinary_hours)} />
                          <DetailRow label="Övertid 1" value={fmtNum(row.overtime_1_hours)} />
                          <DetailRow label="Övertid 2" value={fmtNum(row.overtime_2_hours)} />
                          <DetailRow label="Komptid" value={fmtNum(row.comp_earned_hours)} />
                          <DetailRow label="Totalt arbete" value={fmtNum(row.total_work_hours)} bold />
                          <div style={{ marginTop: 8 }}>
                            <DetailRow label="Debiterbart" value={fmtNum(row.billable_hours)} />
                            <DetailRow label="Ej debiterbart" value={fmtNum(row.non_billable_hours)} />
                          </div>
                        </Col>
                        <Col md={3}>
                          <h6 style={S.text.label}>Frånvaro (per typ)</h6>
                          {(row.leave_breakdown || []).length === 0 ? (
                            <span style={{ ...S.text.bodySmall, color: S.colors.textMuted }}>Ingen frånvaro</span>
                          ) : (
                            row.leave_breakdown.map((lt) => (
                              <DetailRow key={lt.leave_type_id} label={lt.name || lt.code} value={fmtNum(lt.hours)} />
                            ))
                          )}
                          <DetailRow label="Totalt frånvaro" value={fmtNum(row.total_leave_hours)} bold />
                        </Col>
                        <Col md={3}>
                          <h6 style={S.text.label}>Status</h6>
                          <DetailRow label="Utkast" value={fmtNum(row.draft_hours)} />
                          <DetailRow label="Inskickat" value={fmtNum(row.submitted_hours)} />
                          <DetailRow label="Godkänt" value={fmtNum(row.approved_hours)} />
                          <DetailRow label="Avvisat" value={fmtNum(row.rejected_hours)} />
                        </Col>
                      </Row>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
        {teamTotals && (
          <tfoot>
            <tr style={{ ...S.tableHeaderRow, fontWeight: 600 }}>
              <td style={S.tableCell} />
              <td style={S.tableCell}>Team totalt</td>
              <td style={{ ...S.tableCell, textAlign: 'right' }} colSpan={3}>
                {fmtNum(teamTotals.total_work_hours)}h arbete
              </td>
              <td style={{ ...S.tableCell, textAlign: 'right' }}>{fmtNum(teamTotals.total_leave_hours)}h</td>
              <td style={{ ...S.tableCell, textAlign: 'right' }}>{fmtNum(teamTotals.billable_hours)}h</td>
              <td style={{ ...S.tableCell, textAlign: 'right' }}>
                {teamTotals.total_hours > 0
                  ? fmtPct((teamTotals.approved_hours / teamTotals.total_hours) * 100)
                  : '—'}
              </td>
              <td style={{ ...S.tableCell, textAlign: 'right' }}>{fmtNum(teamTotals.total_hours)}h</td>
              <td style={S.tableCell} />
            </tr>
          </tfoot>
        )}
      </table>
    );
  };

  // ─── Per projekt ───

  const renderPerProject = () => {
    if (!Array.isArray(data) || data.length === 0) {
      return (
        <div style={S.emptyState}>
          <div style={S.emptyStateIcon}><Briefcase size={48} /></div>
          <h4 style={S.emptyStateTitle}>Inga projekt att visa</h4>
          <p style={S.emptyStateDesc}>Inga arbetade timmar för perioden.</p>
        </div>
      );
    }
    return (
      <table style={S.table}>
        <thead>
          <tr style={S.tableHeaderRow}>
            <th style={{ ...S.tableHeaderCell, width: 28 }} />
            <th style={S.tableHeaderCell}>Projekt</th>
            <th style={S.tableHeaderCell}>Kund</th>
            <th style={{ ...S.tableHeaderCell, textAlign: 'right' }}>Timmar</th>
            <th style={{ ...S.tableHeaderCell, textAlign: 'right' }}>Debiterbart</th>
            <th style={{ ...S.tableHeaderCell, textAlign: 'right' }}>Belopp</th>
          </tr>
        </thead>
        <tbody>
          {data.map((proj) => {
            const isExpanded = expandedId === proj.project_id;
            return (
              <React.Fragment key={proj.project_id}>
                <tr
                  style={{ ...S.tableRow, cursor: 'pointer' }}
                  onClick={() => setExpandedId(isExpanded ? null : proj.project_id)}
                >
                  <td style={S.tableCell}>
                    {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </td>
                  <td style={S.tableCell}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <span style={S.colorDot(proj.project_color || CHART_PALETTE[0], 8)} />
                      <span style={S.text.bodySmallMedium}>{proj.project_name}</span>
                      {proj.project_code && (
                        <span style={S.text.label}>{proj.project_code}</span>
                      )}
                    </span>
                  </td>
                  <td style={S.tableCell}>
                    <span style={{ ...S.text.bodySmall, color: proj.customer_name ? S.colors.textPrimary : S.colors.textMuted }}>
                      {proj.customer_name || '—'}
                    </span>
                  </td>
                  <td style={{ ...S.tableCell, textAlign: 'right', ...S.text.number }}>
                    {fmtNum(proj.total_hours)}h
                  </td>
                  <td style={{ ...S.tableCell, textAlign: 'right', ...S.text.number }}>
                    {fmtNum(proj.billable_hours)}h
                  </td>
                  <td style={{ ...S.tableCell, textAlign: 'right', ...S.text.bodySmallMedium }}>
                    {proj.billable_amount > 0
                      ? `${Math.round(proj.billable_amount).toLocaleString('sv-SE')} ${proj.currency || 'kr'}`
                      : '—'}
                  </td>
                </tr>
                {isExpanded && (
                  <tr>
                    <td colSpan={6} style={{ background: S.colors.bgSubtle, padding: '12px 24px' }}>
                      <h6 style={S.text.label}>Anställda på projektet</h6>
                      <table style={{ ...S.table, marginTop: 8 }}>
                        <thead>
                          <tr style={S.tableHeaderRow}>
                            <th style={S.tableHeaderCell}>Anställd</th>
                            <th style={{ ...S.tableHeaderCell, textAlign: 'right' }}>Timmar</th>
                            <th style={{ ...S.tableHeaderCell, textAlign: 'right' }}>Debiterbart</th>
                            <th style={{ ...S.tableHeaderCell, textAlign: 'right' }}>Belopp</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(proj.employees || []).map((emp) => (
                            <tr key={emp.employee_id} style={S.tableRow}>
                              <td style={S.tableCell}>
                                <span style={S.text.bodySmall}>{emp.firstname} {emp.lastname}</span>
                              </td>
                              <td style={{ ...S.tableCell, textAlign: 'right', ...S.text.number }}>{fmtNum(emp.hours)}h</td>
                              <td style={{ ...S.tableCell, textAlign: 'right', ...S.text.number }}>{fmtNum(emp.billable_hours)}h</td>
                              <td style={{ ...S.tableCell, textAlign: 'right' }}>
                                {emp.amount != null
                                  ? `${Math.round(emp.amount).toLocaleString('sv-SE')} ${proj.currency || 'kr'}`
                                  : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    );
  };

  // ─── Per kund ───

  const renderPerCustomer = () => {
    if (!Array.isArray(data) || data.length === 0) {
      return (
        <div style={S.emptyState}>
          <div style={S.emptyStateIcon}><Building2 size={48} /></div>
          <h4 style={S.emptyStateTitle}>Inga kunder att visa</h4>
          <p style={S.emptyStateDesc}>Inga arbetade timmar för perioden.</p>
        </div>
      );
    }
    return (
      <table style={S.table}>
        <thead>
          <tr style={S.tableHeaderRow}>
            <th style={{ ...S.tableHeaderCell, width: 28 }} />
            <th style={S.tableHeaderCell}>Kund</th>
            <th style={{ ...S.tableHeaderCell, textAlign: 'right' }}>Timmar</th>
            <th style={{ ...S.tableHeaderCell, textAlign: 'right' }}>Debiterbart</th>
            <th style={{ ...S.tableHeaderCell, textAlign: 'right' }}>Belopp</th>
          </tr>
        </thead>
        <tbody>
          {data.map((cust) => {
            const key = cust.customer_id || `__none__:${cust.customer_name}`;
            const isExpanded = expandedId === key;
            return (
              <React.Fragment key={key}>
                <tr
                  style={{ ...S.tableRow, cursor: 'pointer' }}
                  onClick={() => setExpandedId(isExpanded ? null : key)}
                >
                  <td style={S.tableCell}>
                    {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </td>
                  <td style={S.tableCell}>
                    <span style={S.text.bodySmallMedium}>{cust.customer_name}</span>
                    {cust.projects?.length > 0 && (
                      <span style={{ ...S.text.label, marginLeft: 8 }}>
                        {cust.projects.length} projekt
                      </span>
                    )}
                  </td>
                  <td style={{ ...S.tableCell, textAlign: 'right', ...S.text.number }}>
                    {fmtNum(cust.total_hours)}h
                  </td>
                  <td style={{ ...S.tableCell, textAlign: 'right', ...S.text.number }}>
                    {fmtNum(cust.billable_hours)}h
                  </td>
                  <td style={{ ...S.tableCell, textAlign: 'right', ...S.text.bodySmallMedium }}>
                    {cust.billable_amount > 0
                      ? `${Math.round(cust.billable_amount).toLocaleString('sv-SE')} ${cust.currency || 'kr'}`
                      : '—'}
                  </td>
                </tr>
                {isExpanded && (
                  <tr>
                    <td colSpan={5} style={{ background: S.colors.bgSubtle, padding: '12px 24px' }}>
                      <h6 style={S.text.label}>Projekt under {cust.customer_name}</h6>
                      <table style={{ ...S.table, marginTop: 8 }}>
                        <thead>
                          <tr style={S.tableHeaderRow}>
                            <th style={S.tableHeaderCell}>Projekt</th>
                            <th style={{ ...S.tableHeaderCell, textAlign: 'right' }}>Timmar</th>
                            <th style={{ ...S.tableHeaderCell, textAlign: 'right' }}>Debiterbart</th>
                            <th style={{ ...S.tableHeaderCell, textAlign: 'right' }}>Belopp</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cust.projects.map((proj) => (
                            <tr key={proj.project_id} style={S.tableRow}>
                              <td style={S.tableCell}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                  <span style={S.colorDot(proj.project_color || CHART_PALETTE[0], 8)} />
                                  <span style={S.text.bodySmall}>{proj.project_name}</span>
                                  {proj.project_code && (
                                    <span style={S.text.label}>{proj.project_code}</span>
                                  )}
                                </span>
                              </td>
                              <td style={{ ...S.tableCell, textAlign: 'right', ...S.text.number }}>{fmtNum(proj.hours)}h</td>
                              <td style={{ ...S.tableCell, textAlign: 'right', ...S.text.number }}>{fmtNum(proj.billable_hours)}h</td>
                              <td style={{ ...S.tableCell, textAlign: 'right' }}>
                                {proj.billable_amount > 0
                                  ? `${Math.round(proj.billable_amount).toLocaleString('sv-SE')} ${cust.currency || 'kr'}`
                                  : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    );
  };

  // ─── Sammanfattning ───

  const renderSummary = () => {
    if (!data || !data.summary) return renderEmpty();
    const { current, previous, delta, topProjects, dailyHeatmap, byDayOfWeek, priorRange } = data.summary;
    const perEmployee = data.perEmployee || [];
    const Tile = ({ label, value, deltaPct }) => (
      <div style={{ ...S.card, padding: 16, height: '100%' }}>
        <div style={S.text.label}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: S.colors.textPrimary, marginTop: 4 }}>{value}</div>
        {deltaPct != null && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 6,
            color: deltaPct >= 0 ? S.colors.success : S.colors.danger,
            fontSize: 12, fontWeight: 500,
          }}>
            {deltaPct >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            {deltaPct >= 0 ? '+' : ''}{fmtNum(deltaPct)}%
            <span style={{ ...S.text.label, marginLeft: 4 }}>vs föreg.</span>
          </div>
        )}
      </div>
    );

    const compareData = [
      { metric: 'Arbete',     Aktuell: current.workHours,     Föregående: previous.workHours },
      { metric: 'Debiterbart', Aktuell: current.billableHours, Föregående: previous.billableHours },
      { metric: 'Frånvaro',   Aktuell: current.leaveHours,    Föregående: previous.leaveHours },
    ];

    const pieData = (topProjects || []).map((p, i) => ({
      name: p.name,
      value: Number(p.hours) || 0,
      color: p.color || CHART_PALETTE[i % CHART_PALETTE.length],
    }));

    // Weekly aggregation derived from the heatmap. Computed on the
    // frontend so the backend response stays day-grain — easier to reuse
    // for the calendar heatmap, easier to add other rollups later.
    const weeklyTrend = aggregateByISOWeek(dailyHeatmap || []);

    // Per-employee stacked bar dataset. Overtime is summed across both
    // tiers so the bar stays scannable; the per-employee table breaks
    // them out separately for payroll precision.
    const employeeMix = (perEmployee || [])
      .filter(r => (r.total_hours || 0) > 0)
      .map(r => ({
        name: `${r.firstname || ''} ${r.lastname || ''}`.trim() || '?',
        Ordinarie: Number(r.ordinary_hours) || 0,
        Övertid: (Number(r.overtime_1_hours) || 0) + (Number(r.overtime_2_hours) || 0),
        Komp: Number(r.comp_earned_hours) || 0,
        Frånvaro: Number(r.total_leave_hours) || 0,
      }))
      .sort((a, b) => (b.Ordinarie + b.Övertid + b.Komp + b.Frånvaro) - (a.Ordinarie + a.Övertid + a.Komp + a.Frånvaro));

    return (
      <div style={{ padding: 20 }}>
        <Row>
          <Col md={3}><Tile label="Totalt timmar" value={`${fmtNum(current.totalHours)}h`} deltaPct={delta.totalHours.pct} /></Col>
          <Col md={3}><Tile label="Debiterbart" value={`${fmtNum(current.billableHours)}h`} deltaPct={delta.billableHours.pct} /></Col>
          <Col md={3}><Tile label="Frånvaro" value={`${fmtNum(current.leaveHours)}h`} deltaPct={delta.leaveHours.pct} /></Col>
          <Col md={3}><Tile label="Anställda med poster" value={String(current.employeeCount)} deltaPct={delta.employeeCount.pct} /></Col>
        </Row>

        {/* Calendar heatmap — full width, the dashboard's centerpiece.
            Surfaces team intensity by day at a glance: gaps, weekend
            work, ramp-ups become obvious. */}
        <Row style={{ marginTop: 24 }}>
          <Col md={12}>
            <div style={{ ...S.card, padding: 16 }}>
              <h3 style={S.cardTitle}>Aktivitet per dag</h3>
              <p style={{ ...S.text.label, marginTop: 0 }}>
                Hela teamet, {dailyHeatmap?.length || 0} dagar
              </p>
              <CalendarHeatmap data={dailyHeatmap || []} />
            </div>
          </Col>
        </Row>

        {/* Weekly trend + day-of-week pattern, side by side. The trend
            answers "are we going up or down"; the DOW bar answers "when
            in the week do we work." */}
        <Row style={{ marginTop: 16 }}>
          <Col md={7}>
            <div style={{ ...S.card, padding: 16 }}>
              <h3 style={S.cardTitle}>Veckotrend</h3>
              {weeklyTrend.length === 0 ? (
                <p style={S.text.label}>För kort intervall för veckotrend.</p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={weeklyTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke={S.colors.borderLight} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => `${fmtNum(v)}h`} />
                    <Bar dataKey="hours" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </Col>
          <Col md={5}>
            <div style={{ ...S.card, padding: 16 }}>
              <h3 style={S.cardTitle}>Per veckodag</h3>
              {(byDayOfWeek || []).every(d => d.hours === 0) ? (
                <p style={S.text.label}>Ingen aktivitet i intervallet.</p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={byDayOfWeek || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke={S.colors.borderLight} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => `${fmtNum(v)}h`} />
                    <Bar dataKey="hours" fill="#10b981" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </Col>
        </Row>

        {/* Per-employee stacked bar — sortable scan of who worked what
            mix this period without scrolling the per-employee table. */}
        {employeeMix.length > 0 && (
          <Row style={{ marginTop: 16 }}>
            <Col md={12}>
              <div style={{ ...S.card, padding: 16 }}>
                <h3 style={S.cardTitle}>Sammansättning per anställd</h3>
                <ResponsiveContainer width="100%" height={Math.max(220, employeeMix.length * 36)}>
                  <BarChart data={employeeMix} layout="vertical" margin={{ left: 24 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={S.colors.borderLight} horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => `${fmtNum(v)}h`} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="Ordinarie" stackId="a" fill="#3b82f6" />
                    <Bar dataKey="Övertid"   stackId="a" fill="#f59e0b" />
                    <Bar dataKey="Komp"      stackId="a" fill="#06b6d4" />
                    <Bar dataKey="Frånvaro"  stackId="a" fill="#94a3b8" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Col>
          </Row>
        )}

        <Row style={{ marginTop: 16 }}>
          <Col md={7}>
            <div style={{ ...S.card, padding: 16 }}>
              <h3 style={S.cardTitle}>Aktuell vs föregående period</h3>
              <p style={{ ...S.text.label, marginTop: 0 }}>
                Föregående: {priorRange?.startDate} – {priorRange?.endDate}
              </p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={compareData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={S.colors.borderLight} />
                  <XAxis dataKey="metric" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(v) => `${fmtNum(v)}h`} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Föregående" fill="#cbd5e1" />
                  <Bar dataKey="Aktuell" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Col>
          <Col md={5}>
            <div style={{ ...S.card, padding: 16 }}>
              <h3 style={S.cardTitle}>Topp 5 projekt</h3>
              {pieData.length === 0 ? (
                <p style={S.text.label}>Inga projekt i intervallet.</p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(d) => `${fmtNum(d.value)}h`}>
                      {pieData.map((p, i) => <Cell key={i} fill={p.color} />)}
                    </Pie>
                    <Tooltip formatter={(v) => `${fmtNum(v)}h`} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </Col>
        </Row>
      </div>
    );
  };

  // ─── Beläggning (existing) ───

  const renderUtilization = () => {
    if (!data || !Array.isArray(data)) return null;
    return (
      <table style={S.table}>
        <thead>
          <tr style={S.tableHeaderRow}>
            <th style={S.tableHeaderCell}>Anställd</th>
            <th style={{ ...S.tableHeaderCell, textAlign: 'right' }}>Arbetade</th>
            <th style={{ ...S.tableHeaderCell, textAlign: 'right' }}>Debiterbara</th>
            <th style={{ ...S.tableHeaderCell, textAlign: 'right' }}>Beläggning</th>
            <th style={{ ...S.tableHeaderCell, textAlign: 'right' }}>Debiteringsgrad</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => {
            const scheduleHours = row.schedule_json
              ? Object.values(JSON.parse(row.schedule_json)).reduce((s, h) => s + h, 0)
              : 40;
            const workedPct = scheduleHours > 0 ? (row.worked_hours / scheduleHours) * 100 : 0;
            const billablePct = row.worked_hours > 0 ? (row.billable_hours / row.worked_hours) * 100 : 0;
            return (
              <tr key={i} style={S.tableRow}>
                <td style={S.tableCell}><span style={S.text.bodySmallMedium}>{row.firstname} {row.lastname}</span></td>
                <td style={{ ...S.tableCell, textAlign: 'right', ...S.text.number }}>{fmtNum(row.worked_hours)}h</td>
                <td style={{ ...S.tableCell, textAlign: 'right', ...S.text.number }}>{fmtNum(row.billable_hours)}h</td>
                <td style={{ ...S.tableCell, textAlign: 'right' }}>
                  <span style={{ color: workedPct >= 80 ? S.colors.success : workedPct >= 60 ? S.colors.warning : S.colors.danger }}>{fmtPct(workedPct)}</span>
                </td>
                <td style={{ ...S.tableCell, textAlign: 'right' }}>
                  <span style={{ color: billablePct >= 70 ? S.colors.success : billablePct >= 50 ? S.colors.warning : S.colors.danger }}>{fmtPct(billablePct)}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  // ─── Tidsfördelning (existing) ───

  const renderDistribution = () => {
    if (!data) return null;
    return (
      <Row>
        <Col md={6} style={{ marginBottom: 16 }}>
          <div style={{ ...S.card, padding: 0 }}>
            <div style={S.cardHeader}><h3 style={S.cardTitle}>Per projekt</h3></div>
            <div style={{ padding: 0 }}>
              {(data.byProject || []).map((row, i) => {
                const total = (data.byProject || []).reduce((s, r) => s + r.hours, 0);
                const pct = total > 0 ? (row.hours / total * 100) : 0;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 20px', borderBottom: `1px solid ${S.colors.borderLight}` }}>
                    <span style={S.colorDot(row.color, 8)} />
                    <span style={{ ...S.text.bodySmall, flex: 1 }}>{row.label}</span>
                    <span style={{ ...S.text.bodySmallMedium, ...S.text.number }}>{fmtNum(row.hours)}h</span>
                    <span style={{ ...S.text.label, width: 40, textAlign: 'right' }}>{fmtPct(pct)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </Col>
        <Col md={6} style={{ marginBottom: 16 }}>
          <div style={{ ...S.card, padding: 0 }}>
            <div style={S.cardHeader}><h3 style={S.cardTitle}>Per veckodag</h3></div>
            <div style={{ padding: 0 }}>
              {(data.byDayOfWeek || []).map((row, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 20px', borderBottom: `1px solid ${S.colors.borderLight}` }}>
                  <span style={{ ...S.text.bodySmall, width: 30 }}>{row.label}</span>
                  <div style={{ flex: 1, height: 16, borderRadius: 4, background: S.colors.bgHover }}>
                    <div style={{ height: '100%', borderRadius: 4, background: 'var(--primary)', width: `${Math.min(100, row.hours / 8 * 100)}%`, transition: 'width 300ms' }} />
                  </div>
                  <span style={{ ...S.text.bodySmallMedium, ...S.text.number, width: 40, textAlign: 'right' }}>{fmtNum(row.hours)}h</span>
                </div>
              ))}
            </div>
          </div>
        </Col>
      </Row>
    );
  };

  const renderTab = () => {
    if (loading) return <div style={{ textAlign: 'center', padding: 40 }}><Spinner animation="border" size="sm" /></div>;
    switch (activeTab) {
      case 'per-employee': return renderPerEmployee();
      case 'per-project':  return renderPerProject();
      case 'per-customer': return renderPerCustomer();
      case 'summary':      return data ? renderSummary() : renderEmpty();
      case 'utilization':  return data ? renderUtilization() : renderEmpty();
      case 'distribution': return data ? renderDistribution() : renderEmpty();
      default: return null;
    }
  };

  const renderEmpty = () => (
    <div style={S.emptyState}>
      <div style={S.emptyStateIcon}><BarChart2 size={48} /></div>
      <h4 style={S.emptyStateTitle}>Ingen data ännu</h4>
      <p style={S.emptyStateDesc}>
        Justera datumintervallet eller godkänn fler tidrapporter för att se rapporter.
      </p>
    </div>
  );

  return (
    <>
      <style>{`
        /* Page chrome — toolbar + navigator live outside the card, on
           the page background. Use flex with row wrap so the controls
           stay sane on narrow widths. */
        .rep-toolbar {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 12px;
        }

        /* Granularity toggle (Vecka / Månad / Anpassad) — segmented
           button group, same visual rhythm as TimesheetPage's day-count
           toggle. */
        .rep-gran-toggle {
          display: inline-flex;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          overflow: hidden;
        }
        .rep-gran-btn {
          padding: 5px 12px;
          font-size: 12px;
          border: none;
          background: white;
          color: #475569;
          cursor: pointer;
          transition: all 120ms ease;
          border-right: 1px solid #e2e8f0;
        }
        .rep-gran-btn:last-child { border-right: none; }
        .rep-gran-btn:hover { background: #f1f5f9; color: #111827; }
        .rep-gran-btn.active {
          background: var(--rgba-primary-2);
          color: var(--bs-primary);
          font-weight: 600;
        }

        /* Period navigator — ◀ button | Denna vecka/månad | ▶ button | label */
        .rep-nav {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          border-bottom: 1px solid #f1f5f9;
        }
        /* When the navigator lives outside the card it doesn't need the
           dividing border or padding — it stands on its own row. */
        .rep-nav-bare {
          padding: 0 0 12px 0;
          border-bottom: none;
        }
        .rep-nav-btn {
          width: 30px;
          height: 30px;
          border: 1px solid #e2e8f0;
          background: white;
          border-radius: 6px;
          color: #475569;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 120ms ease;
        }
        .rep-nav-btn:hover { background: #f1f5f9; color: #111827; }
        .rep-nav-current {
          padding: 5px 12px;
          font-size: 12px;
          border: 1px solid #e2e8f0;
          background: white;
          color: #475569;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: all 120ms ease;
        }
        .rep-nav-current:hover:not(:disabled) {
          background: #f1f5f9;
          color: #111827;
        }
        .rep-nav-current:disabled {
          opacity: 0.5;
          cursor: default;
        }
        .rep-nav-label {
          margin-left: 8px;
          font-size: 13px;
          font-weight: 500;
          color: #1e293b;
          font-variant-numeric: tabular-nums;
        }
        .rep-status-select {
          padding: 5px 8px;
          font-size: 12px;
          border-radius: 6px;
          border: 1px solid #e2e8f0;
          background: white;
          color: #1e293b;
          cursor: pointer;
        }
      `}</style>

      <PageHeader
        icon={BarChart3}
        title="Tidrapporter"
        subtitle="Sammanställd tid per anställd, projekt och period."
      />

      {error && <Alert variant="warning" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {/* ─── Page chrome (outside the card) ───
          Tab selector, granularity toggle, status filter, and exports
          live above the card so the card surface only holds the actual
          report. Same separation pattern Stripe / Linear use — chrome
          on top, content in cards. */}
      <div className="rep-toolbar">
        <Nav variant="pills" activeKey={activeTab} onSelect={setActiveTab} style={{ gap: 4 }}>
          {TABS.map(tab => (
            <Nav.Item key={tab.key}>
              <Nav.Link eventKey={tab.key} style={{ fontSize: 13, padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 4 }}>
                <tab.icon size={14} />
                {tab.label}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto', flexWrap: 'wrap' }}>
          <div className="rep-gran-toggle" role="tablist" aria-label="Periodtyp">
            {[
              { key: 'week',   label: 'Vecka' },
              { key: 'month',  label: 'Månad' },
              { key: 'custom', label: 'Anpassad' },
            ].map(g => (
              <button
                key={g.key}
                type="button"
                className={`rep-gran-btn ${granularity === g.key ? 'active' : ''}`}
                onClick={() => handleGranularityChange(g.key)}
                aria-selected={granularity === g.key}
                role="tab"
              >
                {g.label}
              </button>
            ))}
          </div>
          {['per-employee', 'per-project', 'per-customer', 'summary'].includes(activeTab) && (
            <select
              className="rep-status-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              title="Statusfilter"
            >
              <option value="all">Alla poster</option>
              <option value="approved">Endast godkänt</option>
            </select>
          )}
          <button
            onClick={() => handleExport('csv')}
            style={{ ...S.btnSecondary, padding: '5px 10px', fontSize: 12 }}
            title="Exportera CSV"
          >
            <Download size={14} /> CSV
          </button>
          {['per-employee', 'per-project', 'per-customer', 'summary'].includes(activeTab) && (
            <button
              onClick={() => handleExport('pdf')}
              style={{ ...S.btnSecondary, padding: '5px 10px', fontSize: 12 }}
              title="Exportera PDF"
            >
              <FileText size={14} /> PDF
            </button>
          )}
        </div>
      </div>

      {/* Period navigator — ◀ Idag ▶ stepper. Lives outside the card,
          right below the toolbar, mirroring the TimesheetPage rhythm. */}
      {granularity !== 'custom' && (
        <div className="rep-nav rep-nav-bare">
          <button
            type="button"
            className="rep-nav-btn"
            onClick={() => handleStep(-1)}
            title={granularity === 'week' ? 'Föregående vecka' : 'Föregående månad'}
            aria-label="Föregående period"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            className="rep-nav-current"
            onClick={handleJumpToCurrent}
            disabled={periodOffset === 0}
            title={granularity === 'week' ? 'Hoppa till denna vecka' : 'Hoppa till denna månad'}
          >
            {granularity === 'week' ? 'Denna vecka' : 'Denna månad'}
          </button>
          <button
            type="button"
            className="rep-nav-btn"
            onClick={() => handleStep(1)}
            title={granularity === 'week' ? 'Nästa vecka' : 'Nästa månad'}
            aria-label="Nästa period"
          >
            <ChevronRight size={16} />
          </button>
          <span className="rep-nav-label">
            {labelForRange(granularity, periodOffset, startDate, endDate)}
          </span>
        </div>
      )}

      {granularity === 'custom' && (
        <div className="rep-nav rep-nav-bare">
          <span style={S.text.label}>Från</span>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
            style={{ padding: '5px 8px', fontSize: 12, borderRadius: 6, border: `1px solid ${S.colors.borderDefault}` }} />
          <span style={S.text.label}>till</span>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
            style={{ padding: '5px 8px', fontSize: 12, borderRadius: 6, border: `1px solid ${S.colors.borderDefault}` }} />
        </div>
      )}

      {/* The card now holds only the report content — clear visual
          separation between page chrome and the data being shown. */}
      <div style={{ ...S.card, padding: 0 }}>
        <div style={{ padding: activeTab === 'distribution' ? 20 : 0 }}>
          {renderTab()}
        </div>
      </div>
    </>
  );
};

// ─── ISO week + heatmap helpers ───
//
// Pure JS, no date-fns dep needed. ISO week math copied from the
// well-known formula (Jan 4 always falls in week 1).

function isoWeekFor(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7; // Mon=1..Sun=7
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNum = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return { year: d.getUTCFullYear(), week: weekNum };
}

function aggregateByISOWeek(daily) {
  const by = new Map();
  for (const d of daily) {
    const dt = new Date(d.date + 'T00:00:00Z');
    const { year, week } = isoWeekFor(dt);
    const key = `${year}-W${String(week).padStart(2, '0')}`;
    by.set(key, (by.get(key) || 0) + (Number(d.hours) || 0));
  }
  return Array.from(by, ([label, hours]) => ({ label, hours }));
}

// Calendar heatmap — GitHub-style intensity grid. SVG-based so it
// renders sharply at any zoom level and keeps the bundle small (no
// extra heatmap dep).
const CalendarHeatmap = ({ data }) => {
  if (!Array.isArray(data) || data.length === 0) {
    return <p style={{ ...S.text.label, marginTop: 0 }}>Ingen data.</p>;
  }
  // Compute intensity buckets from the data so a quiet team and a busy
  // one both get a useful gradient.
  const max = Math.max(1, ...data.map(d => d.hours));
  const intensityFor = (h) => {
    if (h === 0) return '#f1f5f9';
    const ratio = h / max;
    if (ratio < 0.25) return '#dbeafe';
    if (ratio < 0.5)  return '#93c5fd';
    if (ratio < 0.75) return '#3b82f6';
    return '#1d4ed8';
  };

  // Build week columns: each column is a Mon..Sun column with up to 7
  // cells. Pad the leading week so the first column starts on Monday.
  const cellSize = 14;
  const gap = 2;
  const cols = [];
  let currentCol = [];
  for (let i = 0; i < data.length; i++) {
    const dt = new Date(data[i].date + 'T00:00:00Z');
    const dow = dt.getUTCDay() || 7; // Mon=1..Sun=7
    if (i === 0) {
      // Leading pad — empty cells from Monday to (dow-1)
      for (let p = 1; p < dow; p++) currentCol.push(null);
    }
    currentCol.push(data[i]);
    if (dow === 7) {
      cols.push(currentCol);
      currentCol = [];
    }
  }
  if (currentCol.length > 0) cols.push(currentCol);

  const width = cols.length * (cellSize + gap);
  const height = 7 * (cellSize + gap) + 18; // + room for week-day labels

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg width={width + 30} height={height} style={{ display: 'block' }}>
        {/* Day-of-week labels (Mon, Wed, Fri) */}
        {['Mån', 'Ons', 'Fre'].map((label, i) => (
          <text
            key={label}
            x={0}
            y={(i * 2 + 1) * (cellSize + gap) + 11}
            fontSize="9"
            fill="#64748b"
          >
            {label}
          </text>
        ))}
        <g transform="translate(28, 0)">
          {cols.map((col, ci) => col.map((cell, ri) => {
            if (!cell) return null;
            const x = ci * (cellSize + gap);
            const y = ri * (cellSize + gap);
            return (
              <rect
                key={`${ci}-${ri}`}
                x={x}
                y={y}
                width={cellSize}
                height={cellSize}
                rx={2}
                fill={intensityFor(cell.hours)}
              >
                <title>{`${cell.date}: ${Math.round(cell.hours * 10) / 10}h`}</title>
              </rect>
            );
          }))}
        </g>
      </svg>
      {/* Legend */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 11, color: '#64748b' }}>
        <span>Mindre</span>
        {['#f1f5f9', '#dbeafe', '#93c5fd', '#3b82f6', '#1d4ed8'].map(c => (
          <span key={c} style={{ width: 12, height: 12, background: c, borderRadius: 2, display: 'inline-block' }} />
        ))}
        <span>Mer</span>
      </div>
    </div>
  );
};

// Inline mini bar chart of daily hours per row. Bars in the table
// surface the *pattern* (front-loaded weeks, weekend work, gaps) that
// flat numbers hide. No axes, no tooltips beyond the title — keep it
// scannable; the drill-down has the precise breakdown.
const DailySparkline = ({ daily }) => {
  if (!Array.isArray(daily) || daily.length === 0) return null;
  const max = Math.max(...daily, 1);
  const total = daily.reduce((s, h) => s + h, 0);
  return (
    <div
      title={`${daily.length} dagar, ${Math.round(total * 10) / 10}h totalt`}
      style={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 22, paddingTop: 2 }}
    >
      {daily.map((h, i) => {
        const heightPct = h > 0 ? Math.max((h / max) * 100, 6) : 0;
        return (
          <span
            key={i}
            style={{
              flex: 1,
              minWidth: 1,
              height: `${heightPct}%`,
              background: h === 0 ? '#e5e7eb' : (h > 8 ? '#dc2626' : '#3b82f6'),
              borderRadius: 1,
              alignSelf: 'flex-end',
            }}
          />
        );
      })}
    </div>
  );
};

// Donut showing the mix of debiterbart / ej debiterbart / frånvaro for
// one employee in the period. Uses data already present in the row.
const EmployeeMixDonut = ({ row }) => {
  const slices = [
    { name: 'Debiterbart',     value: Number(row.billable_hours) || 0,     color: '#10b981' },
    { name: 'Ej debiterbart',  value: Number(row.non_billable_hours) || 0, color: '#3b82f6' },
    { name: 'Frånvaro',        value: Number(row.total_leave_hours) || 0,  color: '#f59e0b' },
  ].filter(s => s.value > 0);
  if (slices.length === 0) {
    return <span style={{ ...S.text.bodySmall, color: S.colors.textMuted }}>Inga timmar</span>;
  }
  return (
    <ResponsiveContainer width="100%" height={140}>
      <PieChart>
        <Pie data={slices} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={36} outerRadius={60} paddingAngle={2}>
          {slices.map((s, i) => <Cell key={i} fill={s.color} />)}
        </Pie>
        <Tooltip formatter={(v, n) => [`${Math.round(v * 100) / 100}h`, n]} />
      </PieChart>
    </ResponsiveContainer>
  );
};

// Small helper component for the drill-down rows.
const DetailRow = ({ label, value, bold = false }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    padding: '3px 0',
    fontSize: 12,
    fontWeight: bold ? 600 : 400,
    borderTop: bold ? `1px solid ${S.colors.borderLight}` : 'none',
    marginTop: bold ? 4 : 0,
    paddingTop: bold ? 6 : 3,
  }}>
    <span style={{ color: bold ? S.colors.textPrimary : S.colors.textMuted }}>{label}</span>
    <span style={{ fontVariantNumeric: 'tabular-nums', color: S.colors.textPrimary }}>{value}h</span>
  </div>
);

export default ReportsPage;
